<template>
  <button
    class="btn-cmp body-xl-bold"
    :class="[variant, size, { disabled: disabled, loading: isLoading, 'no-hover': disableHover, mobile: isMobileLayout, 'full-size': isFullSize }]"
    :disabled="disabled || isLoading"
    @click.stop="emit('click')"
  >
    <LoadingSpinner v-if="isLoading" class="spinner" :size="'mini'" :is-mini-white="variant === ButtonVariant.PRIMARY" />
    <div>
      <slot>{{ text }}</slot>
    </div>
  </button>
</template>

<script setup lang="ts">
import { useViewPort } from '@/composables'
import { BaseButton, ButtonSize, ButtonVariant } from '@/styles/types'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'

const { isMobileLayout } = useViewPort()

withDefaults(defineProps<BaseButton>(), {
  variant: ButtonVariant.PRIMARY,
  size: ButtonSize.BIG,
  disabled: false,
  isFullSize: false,
  isLoading: false
})

const emit = defineEmits(['click'])
</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.btn-cmp {
  color: $white;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: 12px;
  width: fit-content;
  height: 100%;
  padding: 12px 16px;
  margin: 0;
  cursor: pointer;
  user-select: none;
  gap: 8px;

  &.loading {
    pointer-events: none;
  }

  .spinner {
    height: 1rem;
  }

  &.full-size {
    width: 100%;
  }

  &.primary {
    background-color: $rebranding-primary-500;

    &.disabled:not(.loading) {
      background-color: $rebranding-grey-500;
    }

    &:not(.no-hover):hover {
      background-color: $rebranding-primary-600;
    }
  }

  &.secondary {
    background-color: $rebranding-primary-50;
    color: $rebranding-primary-500;

    &.disabled:not(.loading) {
      color: $rebranding-grey-500;
      background-color: $rebranding-grey-300;
    }

    &:not(.no-hover):hover {
      background-color: $rebranding-primary-100;
    }
  }

  // 3th button
  &.tertiary {
    color: $rebranding-primary-500;
    background-color: transparent;

    &.disabled:not(.loading) {
      color: $rebranding-grey-500;
    }

    &:not(.no-hover):hover {
      background-color: $rebranding-primary-50;
    }
  }

  &.medium {
    font-size: 16px;
    padding: 8px 12px;
    border-radius: 12px;
  }

  &.small {
    font-size: 12px;
    padding: 8px 10px;

    border-radius: 8px;
  }

  &.mobile {
    font-size: 16px;
  }
}
</style>
